import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import MyScripts from './panel/MyScripts';
import Support from './panel/Support';
import AdminScripts from './panel/AdminScripts';
import AdminDocs from './panel/AdminDocs';
import AdminSupportCategories from './panel/AdminSupportCategories';
import Users from './admin/Users';
import ScriptRoles from './admin/ScriptRoles';
import Roles from './admin/Roles';
import Partners from './admin/Partners';
import Releases from './admin/Releases';
import './Panel.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Panel() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0 });
  const adminButtonRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/user`, {
        withCredentials: true
      });
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      navigate('/');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.get(`${API_URL}/auth/logout`, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return <div className="loading-panel">Loading...</div>;
  }

  return (
    <div className="panel">
      <aside className="panel-sidebar glass-strong">
        <div className="panel-logo">
          <Link to="/" aria-label="Go to home">
            <img src="/logo.png" alt="DevCore" className="panel-logo-image" />
          </Link>
        </div>

        <div className="panel-user">
          <img 
            src={user?.avatar ? `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png` : '/default-avatar.png'} 
            alt={user?.username}
            className="panel-user-avatar"
          />
          <div className="panel-user-info">
            <h3>{user?.username}</h3>
            <span className={`role-badge ${user?.role}`}>{user?.role}</span>
          </div>
        </div>

        <nav className="panel-nav">
          <Link to="/panel" className="panel-nav-item">
            My Scripts
          </Link>

          {user?.role === 'admin' && (
            <>
              <div className="nav-divider">Admin</div>
              <div className="admin-dropdown">
                <div 
                  ref={adminButtonRef}
                  className="admin-dropdown-trigger panel-nav-item"
                  onClick={(e) => {
                    setAdminMenuOpen(!adminMenuOpen);
                    if (!adminMenuOpen && adminButtonRef.current) {
                      const rect = adminButtonRef.current.getBoundingClientRect();
                      setDropdownPosition({ top: rect.top });
                    }
                  }}
                >
                  Admin options
                  <span className={`dropdown-arrow ${adminMenuOpen ? 'open' : ''}`}>▼</span>
                </div>
              </div>
              <div className="nav-divider"></div>
            </>
          )}

          {user?.role === 'admin' && adminMenuOpen && createPortal(
            <div 
              className="admin-dropdown-menu open"
              style={{ top: `${dropdownPosition.top}px` }}
            >
              <Link to="/panel/admin/docs" className="admin-dropdown-item" onClick={() => setAdminMenuOpen(false)}>
                Manage Documentation
              </Link>
              <Link to="/panel/admin/users" className="admin-dropdown-item" onClick={() => setAdminMenuOpen(false)}>
                User Management
              </Link>
              <Link to="/panel/admin/script-roles" className="admin-dropdown-item" onClick={() => setAdminMenuOpen(false)}>
                Script Roles
              </Link>
              <Link to="/panel/admin/roles" className="admin-dropdown-item" onClick={() => setAdminMenuOpen(false)}>
                Roles & Permissions
              </Link>
              <Link to="/panel/admin/partners" className="admin-dropdown-item" onClick={() => setAdminMenuOpen(false)}>
                Partners
              </Link>
              <Link to="/panel/admin/releases" className="admin-dropdown-item" onClick={() => setAdminMenuOpen(false)}>
                Releases
              </Link>
            </div>,
            document.body
          )}

          <Link to="/" className="panel-nav-item">
            Back to Home
          </Link>
          
          <button onClick={handleLogout} className="panel-nav-item logout-btn">
            Logout
          </button>
        </nav>
      </aside>

      <main className="panel-content">
        <Routes>
          <Route path="/" element={<MyScripts user={user} />} />
          <Route path="/support" element={<Support user={user} />} />
          {user?.role === 'admin' && (
            <>
              <Route path="/admin/scripts" element={<AdminScripts />} />
              <Route path="/admin/docs" element={<AdminDocs />} />
              <Route path="/admin/support-categories" element={<AdminSupportCategories />} />
              <Route path="/admin/users" element={<Users user={user} />} />
              <Route path="/admin/script-roles" element={<ScriptRoles />} />
              <Route path="/admin/roles" element={<Roles />} />
              <Route path="/admin/partners" element={<Partners />} />
              <Route path="/admin/releases" element={<Releases />} />
            </>
          )}
        </Routes>
      </main>
    </div>
  );
}

export default Panel;
